import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChatMessage } from './types';
import '../../styles/chatTimeline.css';

/** 节点数据（预计算） */
interface TimelineNode {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly time: string;
  readonly preview: string;
  /** 0~1 标准化位置 */
  readonly normalizedPos: number;
}

interface ChatTimelineProps {
  readonly messages: readonly ChatMessage[];
  readonly chatAreaRef: React.RefObject<HTMLDivElement>;
}

/** 最大预览长度 */
const MAX_PREVIEW_LEN = 120;

/** 紧凑模式阈值（超过此数量自动切换横线样式） */
const COMPACT_THRESHOLD = 40;

/** 截取预览文本 */
const truncatePreview = (content: string): string => {
  // 移除 markdown 标记和多余空白
  const clean = content
    .replace(/```[\s\S]*?```/g, '[代码块]')
    .replace(/!\[.*?\]\(.*?\)/g, '[图片]')
    .replace(/\[(.+?)\]\(.*?\)/g, '$1')
    .replace(/[#*_~`>]/g, '')
    .replace(/\n{2,}/g, '\n')
    .trim();

  if (clean.length <= MAX_PREVIEW_LEN) return clean;
  return clean.slice(0, MAX_PREVIEW_LEN) + '…';
};

/** 格式化时间 */
const formatTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return '';
  }
};

export const ChatTimeline: React.FC<ChatTimelineProps> = ({ messages, chatAreaRef }) => {
  const barRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltipTop, setTooltipTop] = useState<number>(0);
  const [viewportRange, setViewportRange] = useState<{ top: number; height: number }>({ top: 0, height: 100 });

  // ─── 计算节点数据 ─────────────────────

  const nodes: readonly TimelineNode[] = useMemo(() => {
    if (messages.length === 0) return [];
    return messages.map((m, i) => ({
      id: m.id,
      role: m.role,
      time: formatTime(m.dateCreated),
      preview: truncatePreview(m.content),
      normalizedPos: messages.length === 1 ? 0.5 : i / (messages.length - 1),
    }));
  }, [messages]);

  const isCompact = nodes.length > COMPACT_THRESHOLD;

  // ─── 更新可视区域指示器 ─────────────────────

  const updateViewportIndicator = useCallback(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea || messages.length === 0) return;

    const { scrollTop, scrollHeight, clientHeight } = chatArea;
    if (scrollHeight <= clientHeight) {
      setViewportRange({ top: 0, height: 100 });
      return;
    }

    const topPercent = (scrollTop / scrollHeight) * 100;
    const heightPercent = (clientHeight / scrollHeight) * 100;
    setViewportRange({ top: topPercent, height: heightPercent });
  }, [chatAreaRef, messages.length]);

  // ─── 监听滚动来更新指示器 ─────────────────────

  useEffect(() => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;

    updateViewportIndicator();
    chatArea.addEventListener('scroll', updateViewportIndicator, { passive: true });
    return () => chatArea.removeEventListener('scroll', updateViewportIndicator);
  }, [chatAreaRef, updateViewportIndicator]);

  // 消息变化时也刷新
  useEffect(() => {
    updateViewportIndicator();
  }, [messages.length, updateViewportIndicator]);

  // ─── hover 处理 ─────────────────────

  const handleDotEnter = useCallback((e: React.MouseEvent, idx: number) => {
    setHoveredIdx(idx);
    const dot = e.currentTarget as HTMLElement;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // 计算 dot 相对于 wrapper 的垂直偏移
    const wrapperRect = wrapper.getBoundingClientRect();
    const dotRect = dot.getBoundingClientRect();
    const topInWrapper = dotRect.top - wrapperRect.top + dotRect.height / 2;
    setTooltipTop(topInWrapper);
  }, []);

  const handleDotLeave = useCallback(() => {
    setHoveredIdx(null);
  }, []);

  // ─── 点击滚动到对应消息 ─────────────────────

  const handleDotClick = useCallback((idx: number) => {
    const chatArea = chatAreaRef.current;
    if (!chatArea) return;
    const msg = messages[idx];
    if (!msg) return;

    const el = chatArea.querySelector(`[data-message-id="${CSS.escape(msg.id)}"]`);
    if (!el) return;

    // 滚动到消息位置（留出顶部一点余量）
    const messageTop = (el as HTMLElement).offsetTop;
    chatArea.scrollTo({
      top: Math.max(0, messageTop - 40),
      behavior: 'smooth',
    });

    setHoveredIdx(null);
  }, [chatAreaRef, messages]);

  // ─── 不够消息时隐藏 ─────────────────────

  if (nodes.length < 2) return null;

  const hoveredNode = hoveredIdx !== null ? nodes[hoveredIdx] : null;

  return (
    <div
      ref={wrapperRef}
      className={`chat-timeline-wrapper${hoveredNode ? ' has-tooltip' : ''}`}
    >
      <div ref={barRef} className={`chat-timeline-bar${isCompact ? ' compact' : ''}`}>
        {/* 可视区域指示条 */}
        <div
          className="chat-timeline-viewport"
          style={{
            top: `calc(8px + (100% - 16px) * ${viewportRange.top / 100})`,
            height: `calc((100% - 16px) * ${viewportRange.height / 100})`,
          }}
        />

        {/* 轨道和节点 */}
        <div className="chat-timeline-track">
          {nodes.map((node, idx) => (
            <button
              key={node.id}
              type="button"
              className={`chat-timeline-dot ${node.role}`}
              style={{ '--n': node.normalizedPos } as React.CSSProperties}
              onMouseEnter={e => handleDotEnter(e, idx)}
              onMouseLeave={handleDotLeave}
              onClick={() => handleDotClick(idx)}
              title=""
            />
          ))}
        </div>
      </div>

      {/* Tooltip — absolute 相对于 wrapper，始终在时间轴左侧 */}
      <div
        ref={tooltipRef}
        className={`chat-timeline-tooltip${hoveredNode ? ' visible' : ''}`}
        style={{ top: tooltipTop }}
      >
        {hoveredNode && (
          <div className="chat-timeline-tooltip-inner">
            <div className="chat-timeline-tooltip-time">{hoveredNode.time}</div>
            <span className={`chat-timeline-tooltip-role ${hoveredNode.role}`}>
              {hoveredNode.role === 'user' ? '我' : '助手'}
            </span>
            <div className="chat-timeline-tooltip-content">{hoveredNode.preview}</div>
          </div>
        )}
      </div>
    </div>
  );
};
